(() => {
  const CONSENT_KEY = 'black_natasha_cookie_consent';
  let quickAddReady = false;

  const setupCarousel = (carousel) => {
    if (!carousel || carousel.dataset.enhanced === 'true') return;
    const slides = [...carousel.querySelectorAll('.bn-hero__slide')];
    const dots = [...carousel.querySelectorAll('[data-carousel-dot]')];
    const previous = carousel.querySelector('[data-carousel-previous]');
    const next = carousel.querySelector('[data-carousel-next]');
    const pause = carousel.querySelector('[data-carousel-pause]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let manuallyPaused = reducedMotion.matches;
    if (!slides.length) return;

    carousel.dataset.enhanced = 'true';
    let active = 0;
    let timer;

    const show = (index) => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === active;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
        slide.tabIndex = selected ? 0 : -1;
      });
      dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === active;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-current', selected ? 'true' : 'false');
      });
    };

    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (!manuallyPaused && !reducedMotion.matches && !carousel.matches(':focus-within, :hover') && !document.hidden) {
        timer = window.setInterval(() => show(active + 1), 6000);
      }
    };

    previous?.addEventListener('click', () => { show(active - 1); start(); });
    next?.addEventListener('click', () => { show(active + 1); start(); });
    dots.forEach((dot, index) => dot.addEventListener('click', () => { show(index); start(); }));
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', () => requestAnimationFrame(start));
    pause?.addEventListener('click', () => {
      manuallyPaused = !manuallyPaused;
      pause.setAttribute('aria-pressed', String(manuallyPaused));
      pause.textContent = manuallyPaused ? pause.dataset.playLabel : pause.dataset.pauseLabel;
      start();
    });
    reducedMotion.addEventListener('change', start);
    document.addEventListener('visibilitychange', start);
    if (pause) {
      pause.setAttribute('aria-pressed', String(manuallyPaused));
      pause.textContent = manuallyPaused ? pause.dataset.playLabel : pause.dataset.pauseLabel;
    }
    show(0);
    start();
  };

  const marketingAllowed = () => {
    try {
      return JSON.parse(localStorage.getItem(CONSENT_KEY))?.marketing === true;
    } catch (_error) {
      return false;
    }
  };

  const loadConsentEmbeds = (container) => {
    const frames = [...container.querySelectorAll('iframe[data-consent-src]')];
    if (!('IntersectionObserver' in window)) {
      frames.forEach((frame) => { frame.src = frame.dataset.consentSrc; });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const frame = entry.target;
        if (!frame.hasAttribute('src')) frame.src = frame.dataset.consentSrc;
        observer.unobserve(frame);
      });
    }, { rootMargin: '500px 0px' });
    frames.forEach((frame) => observer.observe(frame));
  };

  const updateExternalFeeds = (allowed = marketingAllowed()) => {
    document.querySelectorAll('[data-bn-social-feed]').forEach((feed) => {
      const rail = feed.querySelector('[data-social-rail]');
      const consent = feed.querySelector('[data-social-consent]');
      if (!rail || !consent) return;
      if (allowed) {
        rail.hidden = false;
        consent.hidden = true;
        loadConsentEmbeds(rail);
      } else {
        rail.hidden = true;
        consent.hidden = false;
      }
    });

    document.querySelectorAll('[data-google-live]').forEach((embed) => {
      embed.hidden = !allowed;
      if (allowed) loadConsentEmbeds(embed);
    });
  };

  const setupAutoRail = (rail) => {
    if (!rail || rail.dataset.autoRailReady === 'true') return;
    rail.dataset.autoRailReady = 'true';
    const section = rail.closest('.bn-hot');
    const pause = section?.querySelector('[data-bn-rail-pause]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    let manuallyPaused = reducedMotion.matches;
    let pointerDown = false;
    let visible = false;
    let timer;
    const stop = () => window.clearInterval(timer);
    const updatePause = () => {
      if (!pause) return;
      pause.setAttribute('aria-pressed', String(manuallyPaused));
      pause.textContent = manuallyPaused ? pause.dataset.playLabel : pause.dataset.pauseLabel;
    };
    const move = (forward = true) => {
      const card = rail.querySelector('.bn-product-card');
      if (!card) return;
      const step = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const current = Math.min(maximum, Math.abs(rail.scrollLeft));
      const direction = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
      let target;
      if (forward) target = current >= maximum - 12 ? 0 : Math.min(maximum, current + step);
      else target = current <= 12 ? maximum : Math.max(0, current - step);
      rail.scrollTo({ left: target * direction, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    };
    const start = () => {
      stop();
      if (manuallyPaused || pointerDown || !visible || document.hidden || section?.matches(':focus-within') || (finePointer.matches && rail.matches(':hover')) || rail.scrollWidth <= rail.clientWidth + 12) return;
      timer = window.setInterval(() => move(), 4500);
    };
    rail.addEventListener('mouseenter', stop);
    rail.addEventListener('mouseleave', start);
    section?.addEventListener('focusin', stop);
    section?.addEventListener('focusout', () => requestAnimationFrame(start));
    rail.addEventListener('pointerdown', () => { pointerDown = true; stop(); });
    const release = () => { pointerDown = false; start(); };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    section?.querySelector('[data-bn-rail-next]')?.addEventListener('click', () => { move(); start(); });
    section?.querySelector('[data-bn-rail-previous]')?.addEventListener('click', () => { move(false); start(); });
    pause?.addEventListener('click', () => { manuallyPaused = !manuallyPaused; updatePause(); start(); });
    const preferenceChanged = () => { manuallyPaused = reducedMotion.matches; updatePause(); start(); };
    reducedMotion.addEventListener('change', preferenceChanged);
    document.addEventListener('visibilitychange', start);
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; start(); });
    observer.observe(rail);
    const resize = new ResizeObserver(start);
    resize.observe(rail);
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(rail)) return;
      stop();
      observer.disconnect();
      resize.disconnect();
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      reducedMotion.removeEventListener('change', preferenceChanged);
      document.removeEventListener('visibilitychange', start);
    });
    updatePause();
    start();
  };

  const setupQuickAdd = () => {
    if (quickAddReady) return;
    quickAddReady = true;
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.bn-product-card__form');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      if (!button || button.disabled) return;
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'מוסיף לסל…';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('add-to-cart-failed');
        const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((result) => result.json());
        document.querySelectorAll('.bn-cart-count, [data-bn-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
        });
        const toast = document.querySelector('[data-bn-cart-notice]');
        if (toast) {
          toast.hidden = false;
        }
      } catch (_error) {
        button.textContent = 'לא הצלחנו להוסיף — נסו שוב';
        window.setTimeout(() => { button.textContent = original; }, 3000);
      } finally {
        button.disabled = false;
        if (button.textContent === 'מוסיף לסל…') button.textContent = original;
      }
    });
  };

  const initialize = () => {
    document.querySelectorAll('[data-bn-carousel]').forEach(setupCarousel);
    document.querySelectorAll('[data-bn-auto-rail]').forEach(setupAutoRail);
    updateExternalFeeds();
    setupQuickAdd();
  };

  document.addEventListener('bn:consent-updated', (event) => updateExternalFeeds(Boolean(event.detail?.marketing)));
  document.addEventListener('shopify:section:load', initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();
